import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useLocalCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { headerNavForRole } from '../config/features';
import useFeatureBadges from '../hooks/useFeatureBadges';
import './Header.css';

/* --- Icons --- */
const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

const CartIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 001.98 1.61h9.72a2 2 0 001.98-1.61L23 6H6" />
    </svg>
);

const UserIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
);

const ChevronDownIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const MenuIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
    </svg>
);

const CloseIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const TruckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="1" y="3" width="15" height="13" rx="1" /><path d="M16 8h4l3 5v3h-7V8z" />
        <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
);

/* -------------------------------------------------------------------------- */

const MicrosoftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 21 21" fill="none" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
        <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
);

const GoogleIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

const Header = () => {
    const { user, logout, loginWithMicrosoft, loginWithGoogle } = useAuth();
    const { cartCount } = useLocalCart();
    const { theme, toggle: toggleTheme } = useTheme();
    const navigate = useNavigate();

    const [searchQuery, setSearchQuery]         = useState('');
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isUserMenuOpen, setUserMenuOpen]     = useState(false);
    const [isLoginDropOpen, setLoginDropOpen]   = useState(false);
    const [ssoLoading, setSsoLoading]           = useState(false);

    const userMenuRef  = useRef(null);
    const loginDropRef = useRef(null);

    const homeLink = user ? '/dashboard' : '/';

    // One list, rendered by both the desktop strip and the mobile drawer —
    // they used to be hand-duplicated and drifted apart.
    const navItems = headerNavForRole(user?.role);

    // Counts of work waiting, so staff see it from any screen rather than only
    // after opening the admin area. No-ops for roles without those endpoints.
    const badges = useFeatureBadges(user?.role);
    const badgeTotal = (badges.pending || 0) + (badges.brandstore || 0);

    const badgeFor = (item) =>
        item.badgeKey === 'total' && badgeTotal > 0 ? badgeTotal : 0;

    /* Close dropdowns when clicking outside */
    useEffect(() => {
        const handle = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
            if (loginDropRef.current && !loginDropRef.current.contains(e.target)) {
                setLoginDropOpen(false);
            }
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, []);

    const googleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            setSsoLoading(true);
            setLoginDropOpen(false);
            try {
                await loginWithGoogle(tokenResponse.access_token);
            } catch {
                setSsoLoading(false);
            }
        },
        onError: () => setSsoLoading(false),
    });

    const handleMicrosoftLogin = async () => {
        setLoginDropOpen(false);
        setSsoLoading(true);
        try {
            await loginWithMicrosoft();
        } catch {
            setSsoLoading(false);
        }
    };

    /* Lock body scroll when mobile menu is open */
    useEffect(() => {
        document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isMobileMenuOpen]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const q = searchQuery.trim();
        if (q) {
            navigate(`/products?q=${encodeURIComponent(q)}`);
            setMobileMenuOpen(false);
        }
    };

    const handleLogout = () => {
        logout();
        setUserMenuOpen(false);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <header className="header" role="banner">

                {/* ── Main row ─────────────────────────────────────────── */}
                <div className="header__main">
                    <div className="header__main-inner">

                        {/* Logo */}
                        <Link to={homeLink} className="header__logo" aria-label="eYantra Commerce — go to home">
                            <span className="header__logo-text">eYantra</span>
                        </Link>

                        {/* Pan-India delivery badge — desktop only */}
                        {/*<div className="header__delivery-badge" aria-label="Ships across India">
                            <TruckIcon />
                            <span className="header__delivery-label">
                                <span className="header__delivery-sub">Delivering across</span>
                                <span className="header__delivery-city">India</span>
                            </span>
                        </div>*/}

                        {/* Search */}
                        <form
                            className="header__search-wrap"
                            onSubmit={handleSearchSubmit}
                            role="search"
                            aria-label="Search products"
                        >
                            <input
                                type="search"
                                className="header__search-input"
                                placeholder="Search for products, brands and more…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                aria-label="Search"
                                autoComplete="off"
                            />
                            <button type="submit" className="header__search-btn" aria-label="Submit search">
                                <SearchIcon />
                            </button>
                        </form>

                        {/* Right actions */}
                        <div className="header__actions">

                            {/* User area */}
                            {user ? (
                                <div className="header__user-menu" ref={userMenuRef}>
                                    <button
                                        className="header__user-trigger"
                                        onClick={() => setUserMenuOpen((p) => !p)}
                                        aria-haspopup="true"
                                        aria-expanded={isUserMenuOpen}
                                        aria-label={`Account menu for ${user.username}`}
                                    >
                                        <UserIcon />
                                        <span className="header__user-name">{user.username}</span>
                                        <ChevronDownIcon />
                                    </button>

                                    {isUserMenuOpen && (
                                        <div className="header__user-dropdown" role="menu">
                                            <div className="header__user-dropdown-header">
                                                <span className="header__user-dropdown-greeting">Hi, {user.username}</span>
                                                <span className="header__user-dropdown-role">{user.role?.replace(/_/g, ' ')}</span>
                                            </div>
                                            <div className="header__user-dropdown-divider" />
                                            {/* Cart links live in the nav strip now. This menu keeps only
                                                account-scoped actions, so the same destination isn't offered
                                                twice from the same header. */}
                                            <Link to="/dashboard" className="header__user-dropdown-item" role="menuitem" onClick={() => setUserMenuOpen(false)}>Dashboard</Link>
                                            <div className="header__user-dropdown-divider" />
                                            <button className="header__user-dropdown-item header__user-dropdown-item--danger" role="menuitem" onClick={handleLogout}>Sign out</button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="header__auth-links">
                                    <div className="header__login-dropdown-wrap" ref={loginDropRef}>
                                        <button
                                            className="header__auth-link header__login-btn"
                                            onClick={() => setLoginDropOpen(o => !o)}
                                            disabled={ssoLoading}
                                            aria-haspopup="listbox"
                                            aria-expanded={isLoginDropOpen}
                                        >
                                            {ssoLoading ? 'Signing in…' : 'Login'}
                                            <ChevronDownIcon />
                                        </button>

                                        {isLoginDropOpen && (
                                            <div className="header__login-dropdown" role="listbox">
                                                <button
                                                    className="header__login-dropdown-option"
                                                    onClick={handleMicrosoftLogin}
                                                    role="option"
                                                >
                                                    <MicrosoftIcon />
                                                    Continue with Microsoft
                                                </button>
                                                <button
                                                    className="header__login-dropdown-option"
                                                    onClick={() => { setLoginDropOpen(false); googleLogin(); }}
                                                    role="option"
                                                >
                                                    <GoogleIcon />
                                                    Continue with Google
                                                </button>
                                                <div className="header__login-dropdown-divider" />
                                                <Link
                                                    to="/login"
                                                    className="header__login-dropdown-option"
                                                    role="option"
                                                    onClick={() => setLoginDropOpen(false)}
                                                >
                                                    Login with Email
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                    <Link to="/register" className="header__auth-btn">Sign Up</Link>
                                </div>
                            )}

                            {/* Cart — consumers go to /cart, internal staff go to /cart (staging → cart builder) */}
                            <Link to="/cart" className="header__cart" aria-label={`Cart, ${cartCount} item${cartCount !== 1 ? 's' : ''}`}>
                                <CartIcon />
                                {cartCount > 0 && (
                                    <span className="header__cart-badge" aria-hidden="true">
                                        {cartCount > 99 ? '99+' : cartCount}
                                    </span>
                                )}
                            </Link>

                            {/* Theme toggle — hidden; light theme only for now */}
                            {/* <button
                                className="header__theme-toggle"
                                onClick={toggleTheme}
                                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
                            >
                                {theme === 'dark' ? '☀' : '🌙'}
                            </button> */}

                            {/* Mobile hamburger */}
                            <button
                                className="header__mobile-toggle"
                                onClick={() => setMobileMenuOpen((p) => !p)}
                                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                                aria-expanded={isMobileMenuOpen}
                                aria-controls="mobile-nav"
                            >
                                {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Nav strip (desktop) — no mega menu ──────────────── */}
                <nav className="header__nav" aria-label="Site navigation">
                    <div className="header__nav-inner">
                        {navItems.map(item => {
                            const count = badgeFor(item);
                            return (
                                <Link key={item.key} to={item.to} className="header__nav-item">
                                    {item.label}
                                    {count > 0 && (
                                        <span className="header__nav-badge">
                                            {count > 99 ? '99+' : count}
                                            <span className="sr-only"> items need attention</span>
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                    </div>
                </nav>
            </header>

            {/* ── Mobile overlay ──────────────────────────────────────── */}
            {isMobileMenuOpen && (
                <div className="header__mobile-overlay" id="mobile-nav" role="dialog" aria-label="Mobile navigation" aria-modal="true">
                    <form className="header__mobile-search" onSubmit={handleSearchSubmit} role="search">
                        <div className="header__search-wrap header__search-wrap--mobile">
                            <input
                                type="search"
                                className="header__search-input"
                                placeholder="Search products…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                aria-label="Search"
                                autoComplete="off"
                                autoFocus
                            />
                            <button type="submit" className="header__search-btn" aria-label="Submit search">
                                <SearchIcon />
                            </button>
                        </div>
                    </form>

                    <nav className="header__mobile-nav" aria-label="Mobile navigation">
                        {navItems.map(item => {
                            const count = badgeFor(item);
                            return (
                                <Link
                                    key={item.key}
                                    to={item.to}
                                    className="header__mobile-nav-item"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {item.label}
                                    {count > 0 && (
                                        <span className="header__mobile-nav-badge">
                                            {count > 99 ? '99+' : count}
                                            <span className="sr-only"> items need attention</span>
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                        <Link to="/cart" className="header__mobile-nav-item header__mobile-nav-item--cart" onClick={() => setMobileMenuOpen(false)}>
                            Cart {cartCount > 0 && <span className="header__mobile-cart-count">{cartCount}</span>}
                        </Link>
                    </nav>

                    <div className="header__mobile-auth">
                        {user ? (
                            <>
                                <span className="header__mobile-welcome">Hi, {user.username}</span>
                                <button className="header__mobile-logout" onClick={handleLogout}>Sign out</button>
                            </>
                        ) : (
                            <>
                                <Link to="/login"    className="header__mobile-login-btn"    onClick={() => setMobileMenuOpen(false)}>Login</Link>
                                <Link to="/register" className="header__mobile-register-btn" onClick={() => setMobileMenuOpen(false)}>Sign Up</Link>
                            </>
                        )}
                    </div>
                </div>
            )}

            {isMobileMenuOpen && (
                <div className="header__mobile-backdrop" onClick={() => setMobileMenuOpen(false)} aria-hidden="true" />
            )}
        </>
    );
};

export default Header;